/**
 * What the Armadillo conversation can be answered on, as data in and data
 * out: every email from the research desk a reply may name, and which of
 * them the composer offers first.
 */

import {
  canReplyToRegistryMessage,
  hasRegistryOrderReply,
  type RegistryOrder,
  type RegistryOrderConversation,
  type RegistryOrderMessage,
} from '@/types/registry-documents';

/** One email from the research desk a reply can answer. */
export interface RegistryReplyTarget {
  /** Stable across reads: the order and the email's Message-ID. */
  key: string;
  order: RegistryOrder;
  message: RegistryOrderMessage;
}

/** A target's key: the order and the email's Message-ID. The same shape the
 * `?aside=armadillo&target=` deep link carries. */
export function replyTargetKey(orderId: string, messageId: string | null): string {
  return `${orderId}:${messageId ?? ''}`;
}

/** Every email the API would accept a reply to, oldest first: the desk's
 * own, from a sender the strict rules accepted, with a recorded
 * Message-ID, on an order that was sent. */
export function replyTargetsOf(conversation: RegistryOrderConversation): RegistryReplyTarget[] {
  const orders = new Map(conversation.orders.map((order) => [order.id, order]));
  const targets: RegistryReplyTarget[] = [];
  for (const item of conversation.items) {
    const order = orders.get(item.orderId);
    const message = item.message;
    if (item.kind !== 'message' || !order || !message) {
      continue;
    }
    if (canReplyToRegistryMessage(order, message)) {
      targets.push({ key: replyTargetKey(order.id, message.messageId), order, message });
    }
  }
  return targets;
}

/** The email the composer offers first: the latest one nobody has
 * answered, else the latest one there is. Null when there is none. */
export function defaultReplyTarget(
  targets: readonly RegistryReplyTarget[]
): RegistryReplyTarget | null {
  const unanswered = targets.filter((target) => !target.message.answered);
  return unanswered.at(-1) ?? targets.at(-1) ?? null;
}

/**
 * The email the composer answers, given what was asked for: a target's
 * key names one email; an order id alone (a Reply pill, a link that names
 * no email) means the order's default; a request that names nothing a
 * reply may answer (an unverified sender, an email a re-read dropped)
 * falls back to the conversation's default. Null when there is none.
 */
export function resolveReplyTarget(
  targets: readonly RegistryReplyTarget[],
  request: string | null
): RegistryReplyTarget | null {
  if (request) {
    const exact = targets.find((target) => target.key === request);
    if (exact) {
      return exact;
    }
    // Order ids carry no colon; a Message-ID may.
    const orderId = request.split(':', 1)[0];
    const onOrder = defaultReplyTarget(targets.filter((target) => target.order.id === orderId));
    if (onOrder) {
      return onOrder;
    }
  }
  return defaultReplyTarget(targets);
}

/** How many of the desk's emails still wait for an answer across the
 * report's orders: the Reply pill's count, summed. An answered, delivered
 * or closed order's emails are history. */
export function unansweredCount(conversation: RegistryOrderConversation | null): number {
  if (!conversation) {
    return 0;
  }
  return conversation.orders
    .filter(hasRegistryOrderReply)
    .reduce((sum, order) => sum + order.replyCount, 0);
}

/** "2 prepared, sent at Start", for the empty conversation of a report
 * whose orders have not left yet; null when none is prepared. */
export function preparedNote(orders: readonly Pick<RegistryOrder, 'status'>[]): string | null {
  const prepared = orders.filter((order) => order.status === 'prepared').length;
  return prepared > 0 ? `${prepared} prepared, sent at Start` : null;
}
